import { IconTypes } from '@app/ui'
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react'
import { MenuItemType } from '../types'
import { ROUTES } from '@app/constants'
import {
  AppMenuState,
  CurrentCountryState,
  CurrentUserState,
  guestMode,
  showSignupPromptModal,
  UserAuthState
} from '@app/storage'
import { useSnapshot } from 'valtio'
import { useTranslation } from 'react-i18next'
import { UserAuthStateType } from '@app/types'
import { useTenantConfig } from '@app/hooks/useTenantConfig'
import { useIsDemoTenant } from '@app/hooks/useDemoProfileTipsterId'
import { useSubscriptionsEnabled } from '@app/hooks/useSubscriptionsEnabled'
import { useGetBonusesQuery } from '@app/graphql'
import { isSupportChatMenuPosition, useTawktoConfig } from '@app/hooks/useTawkto'
import { openTawkSupportChat } from '@app/components/SupportChat/supportChatApi'


// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

export const useMenuItems = ({
  setOpenLanguageSelect,
  setTelegramBotSelect,
  setOpenSupportSheet,
  setBecomeTipster,
}: {
  setOpenLanguageSelect: Dispatch<SetStateAction<boolean>>
  setTelegramBotSelect: Dispatch<SetStateAction<boolean>>
  setOpenSupportSheet: Dispatch<SetStateAction<boolean>>
  disableSub: boolean
  setBecomeTipster: Dispatch<SetStateAction<boolean>>
}) => {
  const { opened: appMenuOpened } = useSnapshot(AppMenuState)
  const {
    isTipster,
    isAdmin,
    isTouring,
    tourStage,
    currentTourStage,
    id,
    isTipsterAdmin,
    isGuestTipster,
    isUser,
    countryId,
  } = useSnapshot(CurrentUserState)
  const { id: currentCountryId } = useSnapshot(CurrentCountryState)
  const { t } = useTranslation()
  const isGuest = guestMode()
  const { userLanguageId, languagesList } = useSnapshot<UserAuthStateType>(UserAuthState)
  const { getTenantValue } = useTenantConfig()
  const isDemoTenant = useIsDemoTenant()
  const tawkConfig = useTawktoConfig()
  const liveChatInMenu =
    !!tawkConfig &&
    isSupportChatMenuPosition(tawkConfig.positions) &&
    !isGuest &&
    !isAdmin &&
    (isUser || isTipster)

  const openLiveChatFromMenu = useCallback(() => {
    AppMenuState.opened = false
    openTawkSupportChat()
  }, [])

  const leaderboardEnabled = getTenantValue('featureFlags.leaderboard.enabled', false)
  const becomeTipsterEnabled = getTenantValue('featureFlags.become_tipster.enabled', false)
  const subscriptionsEnabled = useSubscriptionsEnabled()

  // Determine country filter (mirrors useBonuses logic)
  const resolvedCountryId = (currentCountryId as string) || (countryId as string) || null
  const bonusCountryIds = !isAdmin && resolvedCountryId ? [ resolvedCountryId ] : undefined

  // Check if any bonuses exist (users/tipsters hide the menu when empty)
  const { data: bonusCheckData } = useGetBonusesQuery({
    variables: { limit: 1, skip: 0, countryIds: bonusCountryIds },
    fetchPolicy: 'cache-first',
    skip: isAdmin, // admins always see the bonuses menu
  })
  const hasBonuses = isAdmin || (bonusCheckData?.bonuses?.bonuses?.length ?? 0) > 0

  const userMenuItems = useMemo(
    () => [
      {
        title: t('leaderboard.leaderboard'),
        iconType: IconTypes.leaderboard,
        type: MenuItemType.LINK,
        route: ROUTES.leaderboard.short,
        disable: false,
        hide: true, // leaderboard is only for tipsters and admins
      },
      {
        title: t('bonuses.bonus'),
        iconType: IconTypes.bonusIcon,
        type: MenuItemType.LINK,
        route: ROUTES.bonuses.short,
        disable: false,
        hide: !hasBonuses,
        highlighted: true,
      },
      {
        title: t('tip.savedTips'),
        iconType: IconTypes.savedTipIcon,
        route: ROUTES.savedTips.short,
        type: isGuest ? MenuItemType.ACTION : MenuItemType.LINK,
        action: () => {
          if (isGuest) {
            AppMenuState.opened = !appMenuOpened
            showSignupPromptModal({
              message: t('incognito.modal.loginDefaultMessage'),
              title: t('incognito.modal.loginTitle'),
            })()
          }
        },
        disable: isTouring || isGuest,
        hide: false,
      },
      {
        title: t('appMenu.title.subscription'),
        iconType: IconTypes.subscriptionIcon,
        route: ROUTES.tipsterSubscriberList.short,
        type: isGuest ? MenuItemType.ACTION : MenuItemType.LINK,
        action: () => {
          if (isGuest) {
            AppMenuState.opened = !appMenuOpened
            showSignupPromptModal({
              message: t('incognito.modal.loginDefaultMessage'),
              title: t('incognito.modal.loginTitle'),
            })()
          }
        },
        disable: isTouring || isGuest,
        hide: !subscriptionsEnabled,
      },
      {
        title: t('menu.streams'),
        iconType: IconTypes.streamIcon,
        type: MenuItemType.ACTION,
        action: noop,
        disable: true,
        hide: false,
      },
      {
        title: t('common.notification_plural'),
        iconType: IconTypes.notificationIcon,
        route: ROUTES.notifications.short,
        type: isGuest ? MenuItemType.ACTION : MenuItemType.LINK,
        action: () => {
          if (isGuest) {
            AppMenuState.opened = !appMenuOpened
            showSignupPromptModal({
              message: t('incognito.modal.loginDefaultMessage'),
              title: t('incognito.modal.loginTitle'),
            })()
          }
        },
        disable: isTouring || isGuest,
        hide: false,
      },
      {
        title: t('login.languageLabel'),
        iconType: IconTypes.worldIcon,
        type: languagesList.length > 1 ? MenuItemType.ACTION : MenuItemType.LINK,
        hide: false,
        action: () => {
          languagesList.length > 1 ? setOpenLanguageSelect(true) : null
        },
        disable: isTouring,
      },
      {
        title: t('menu.shareApp'),
        iconType: IconTypes.shareIconUpdate,
        type: isGuest ? MenuItemType.ACTION : MenuItemType.SHARE_SOCIAL,
        action: () => {
          if (isGuest) {
            AppMenuState.opened = !appMenuOpened
            showSignupPromptModal({
              message: t('incognito.modal.loginDefaultMessage'),
              title: t('incognito.modal.loginTitle'),
            })()
          }
        },
        disable:
          (isTouring && !(tourStage.subscriptionVideoExplainer || currentTourStage == 'userInviteMenu')) || isGuest,
        hide: false,
      },
      {
        title: t('termsAndConditions.termsAndConditions'),
        iconType: IconTypes.documentIcon,
        type: MenuItemType.LINK,
        route: ROUTES.legal.short,
        disable: false,
        hide: false,
      },
      {
        title: t('menu.helpCenter'),
        iconType: IconTypes.questionSquare,
        type: MenuItemType.ACTION,
        action: () => setOpenSupportSheet(true),
        disable: false,
        hide: false,
      },
      {
        title: t('menu.becomeTipster'),
        iconType: IconTypes.changeRoles,
        type: MenuItemType.ACTION,
        action: () => setBecomeTipster(true),
        hide: !(becomeTipsterEnabled && isUser),
      },
      {
        title: t('menu.liveChat'),
        iconType: IconTypes.liveChatIcon,
        type: MenuItemType.ACTION,
        action: openLiveChatFromMenu,
        disable: isTouring,
        hide: !liveChatInMenu,
      },
    ],
    [
      becomeTipsterEnabled,
      userLanguageId,
      t,
      isTouring,
      tourStage,
      currentTourStage,
      isGuest,
      appMenuOpened,
      hasBonuses,
      liveChatInMenu,
      openLiveChatFromMenu,
      subscriptionsEnabled,
    ],
  )

  const tipsterMenuItems = useMemo(
    () => [
      {
        title: t('leaderboard.leaderboard'),
        iconType: IconTypes.leaderboard,
        type: MenuItemType.LINK,
        route: ROUTES.leaderboard.short,
        disable: isDemoTenant,
        hide: !leaderboardEnabled,
      },
      {
        title: t('menu.management'),
        iconType: IconTypes.folderSettingsIcon,
        type: MenuItemType.DROPDOWN,
        disable: false,
        hide: false,
        children: [
          {
            title: t('common.subscription_plural'),
            iconType: IconTypes.subscriptionIcon,
            type: MenuItemType.LINK,
            route: ROUTES.tipsterSubscriberList.short,
            disable: isTouring || isGuest,
            hide: !subscriptionsEnabled,
          },
          {
            title: t('menu.streams'),
            iconType: IconTypes.streamIcon,
            type: MenuItemType.ACTION,
            action: noop,
            disable: true,
          },
          {
            title: t('installer.tipsters'),
            iconType: IconTypes.subscriberListIcon,
            type: MenuItemType.LINK,
            route: ROUTES.tipsterList.short,
            disable: isDemoTenant,
          },
        ],
      },
      {
        title: t('appMenu.telegramBots'),
        iconType: IconTypes.telegramBotIcon,
        type: MenuItemType.DROPDOWN,
        disable: isGuest || isDemoTenant,
        hide: !(isTipster || isAdmin),
        children: [
          {
            title: t('menu.userInvites'),
            iconType: IconTypes.appInvite,
            type: MenuItemType.ACTION,
            action: noop,
            disable: true,
          },
          {
            title: t('menu.autoShareContent'),
            iconType: IconTypes.shareIconUpdate,
            type: MenuItemType.ACTION,
            action: () => setTelegramBotSelect(true),
            disable: isGuest || isDemoTenant,
          },
        ],
      },
      {
        title: t('tip.savedTips'),
        iconType: IconTypes.savedTipIcon,
        route: ROUTES.savedTips.short,
        type: MenuItemType.LINK,
        disable: isTouring || isGuest,
        hide: false,
      },
      {
        title: t('bonuses.bonus'),
        iconType: IconTypes.bonusIcon,
        type: MenuItemType.LINK,
        route: ROUTES.bonuses.short,
        disable: false,
        hide: !hasBonuses,
        highlighted: true,
      },
      {
        title: t('common.notification_plural'),
        iconType: IconTypes.notificationIcon,
        route: ROUTES.notifications.short,
        type: MenuItemType.LINK,
        disable: isTouring || isGuest,
        hide: false,
      },
      {
        title: t('login.languageLabel'),
        iconType: IconTypes.worldIcon,
        type: languagesList.length > 1 ? MenuItemType.ACTION : MenuItemType.DIVIDER,
        hide: false,
        action: () => {
          languagesList.length > 1 ? setOpenLanguageSelect(true) : null
        },
        disable: isTouring,
      },
      {
        title: t('menu.shareApp'),
        iconType: IconTypes.shareIconUpdate,
        type: isGuest ? MenuItemType.ACTION : MenuItemType.SHARE_SOCIAL,
        action: () => {
          if (isGuest) {
            AppMenuState.opened = !appMenuOpened
            showSignupPromptModal({
              message: t('incognito.modal.loginDefaultMessage'),
              title: t('incognito.modal.loginTitle'),
            })()
          }
        },
        disable:
          (isTouring && !(tourStage.subscriptionVideoExplainer || currentTourStage == 'userInviteMenu')) || isGuest,
        hide: false,
      },
      {
        title: t('termsAndConditions.termsAndConditions'),
        iconType: IconTypes.documentIcon,
        type: MenuItemType.LINK,
        route: ROUTES.legal.short,
        disable: false,
        hide: false,
      },
      {
        title: t('menu.helpCenter'),
        iconType: IconTypes.questionSquare,
        type: MenuItemType.ACTION,
        action: () => setOpenSupportSheet(true),
        disable: false,
        hide: false,
      },
      {
        title: t('menu.liveChat'),
        iconType: IconTypes.liveChatIcon,
        type: MenuItemType.ACTION,
        action: openLiveChatFromMenu,
        disable: isTouring,
        hide: !liveChatInMenu,
      },
    ],
    [
      userLanguageId,
      t,
      isTouring,
      tourStage,
      currentTourStage,
      isAdmin,
      isTipster,
      isTipsterAdmin,
      isGuest,
      id,
      leaderboardEnabled,
      hasBonuses,
      isDemoTenant,
      liveChatInMenu,
      openLiveChatFromMenu,
      subscriptionsEnabled,
    ],
  )

  const superAdminMenuItems = useMemo(
    () => [
      {
        title: t('leaderboard.leaderboard'),
        iconType: IconTypes.leaderboard,
        type: MenuItemType.LINK,
        route: ROUTES.leaderboard.short,
        disable: isDemoTenant,
        hide: !leaderboardEnabled,
      },
      {
        title: t('common.profile'),
        iconType: IconTypes.profile,
        route: `${ROUTES.profile.short}/${id}`,
        type: MenuItemType.LINK,
        disable: isTouring,
      },
      {
        title: t('login.languageLabel'),
        iconType: IconTypes.worldIcon,
        type: languagesList.length > 1 ? MenuItemType.ACTION : MenuItemType.LINK,
        hide: false,
        action: () => {
          languagesList.length > 1 ? setOpenLanguageSelect(true) : null
        },
        disable: isTouring,
      },
      {
        title: t('menu.superAdmin'),
        iconType: IconTypes.gear,
        type: MenuItemType.DROPDOWN,
        disable: false,
        hide: false,
        children: [
          {
            title: t('menu.adverts'),
            iconType: IconTypes.leaderboard,
            type: MenuItemType.LINK,
            route: ROUTES.advertisements.short,
            disable: false,
          },
          {
            title: t('common.bookie'),
            iconType: IconTypes.wallet,
            type: MenuItemType.LINK,
            route: ROUTES.tipsterOnboardingBookie.short,
            disable: false,
          },
          {
            title: t('bonuses.bonus'),
            iconType: IconTypes.bonusIcon,
            type: MenuItemType.LINK,
            route: ROUTES.bonuses.short,
            disable: false,
            highlighted: true,
          },
          {
            title: t('rgBanner.title'),
            iconType: IconTypes.infoOutline,
            type: MenuItemType.LINK,
            route: ROUTES.rgBanners.short,
            disable: false,
          },
        ],
      },
      {
        title: t('menu.management'),
        iconType: IconTypes.folderSettingsIcon,
        type: MenuItemType.DROPDOWN,
        disable: false,
        hide: false,
        children: [
          {
            title: t('appMenu.title.subscription'),
            iconType: IconTypes.subscriptionIcon,
            type: MenuItemType.LINK,
            route: ROUTES.tipsterSubscriberList.short,
            disable: false,
          },
          {
            title: t('menu.streams'),
            iconType: IconTypes.streamIcon,
            type: MenuItemType.ACTION,
            action: noop,
            disable: true,
          },
          {
            title: t('installer.tipsters'),
            iconType: IconTypes.subscriberListIcon,
            type: MenuItemType.LINK,
            route: ROUTES.tipsterList.short,
            disable: isDemoTenant,
          },
        ],
      },
      {
        title: t('appMenu.telegramBots'),
        iconType: IconTypes.telegramBotIcon,
        type: MenuItemType.DROPDOWN,
        disable: isDemoTenant,
        hide: false,
        children: [
          {
            title: t('menu.userInvites'),
            iconType: IconTypes.appInvite,
            type: MenuItemType.ACTION,
            action: noop,
            disable: true,
          },
          {
            title: t('menu.autoShareContent'),
            iconType: IconTypes.shareIconUpdate,
            type: MenuItemType.ACTION,
            action: () => setTelegramBotSelect(true),
            disable: isDemoTenant,
          },
        ],
      },
      {
        title: t('menu.channelInvite'),
        iconType: IconTypes.appInvite,
        type: MenuItemType.SHARE_SOCIAL,
        disable: isTouring || false,
        hide: false,
      },
      {
        title: t('menu.helpCenter'),
        iconType: IconTypes.questionSquare,
        type: MenuItemType.ACTION,
        action: () => setOpenSupportSheet(true),
        disable: false,
        hide: false,
      },
      {
        title: t('termsAndConditions.termsAndConditions'),
        iconType: IconTypes.documentIcon,
        type: MenuItemType.LINK,
        route: ROUTES.legal.short,
        disable: false,
        hide: false,
      },
    ],
    [ userLanguageId, t, isTouring, id, leaderboardEnabled, isDemoTenant ],
  )

  if (isAdmin) return superAdminMenuItems
  if (isTipster || isGuestTipster) return tipsterMenuItems
  return userMenuItems
}
